import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, Repeat, LayoutGrid, Brain } from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const PILLAR_ICONS = [Repeat, LayoutGrid, Brain];

const EffektOverTid = () => {
  const { t } = useTranslation();
  const items = t("effekt.items", { returnObjects: true });
  const pillars = t("effekt.pillars", { returnObjects: true });

  return (
    <section
      data-testid="effekt-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="football" />

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left: items list */}
          <div className="lg:col-span-7">
            <span className="eyebrow">{t("effekt.eyebrow")}</span>
            <h2 className="font-display mt-5 text-xl sm:text-[1.5rem] lg:text-[1.75rem] font-semibold leading-[1.18] text-white glow-text-white uppercase tracking-[-0.005em]">
              {t("effekt.title")}
            </h2>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
              {t("effekt.lead")}
            </p>

            <ul className="mt-10 space-y-3">
              {Array.isArray(items) &&
                items.map((it, i) => (
                  <motion.li
                    key={i}
                    data-testid={`effekt-item-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.015] px-5 py-4 hover:border-[#FF3DCB]/40 transition-colors"
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#FF3DCB]" />
                    <span className="font-display text-base md:text-lg text-white/90">
                      {it}
                    </span>
                  </motion.li>
                ))}
            </ul>
          </div>

          {/* Right: pillars */}
          <div className="lg:col-span-5">
            <div className="glass-strong relative overflow-hidden rounded-2xl p-8 lg:p-10 electric-border">
              <div
                aria-hidden
                className="absolute -right-20 -top-20 h-64 w-64 rounded-full opacity-40 blur-3xl"
                style={{ background: "#9B4DFF" }}
              />
              <span className="eyebrow">{t("effekt.pillars_title")}</span>
              <div className="mt-6 space-y-4">
                {Array.isArray(pillars) &&
                  pillars.map((p, i) => {
                    const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
                    return (
                      <div
                        key={i}
                        data-testid={`effekt-pillar-${i}`}
                        className="flex items-center gap-4 border-b border-white/5 pb-4 last:border-0 last:pb-0"
                      >
                        <span className="font-display number-badge text-3xl font-bold">
                          0{i + 1}
                        </span>
                        <div className="flex flex-1 items-center justify-between">
                          <span className="font-display text-xl font-semibold text-white">
                            {p}
                          </span>
                          <Icon className="h-5 w-5 text-[#6FF6FF]" />
                        </div>
                      </div>
                    );
                  })}
              </div>
              <p className="mt-8 text-sm text-white/65 italic leading-relaxed">
                {t("effekt.outro")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EffektOverTid;
